// @flow
import React, { useState } from 'react';
import classnames from 'classnames';

import ratioUtils from 'Components/utils/ratios/ratios.utils';

import { ArrowButton } from 'Components/frameworks/arrow-button/ArrowButton.jsx';
import { Backdrop } from 'Components/frameworks/backdrop/Backdrop.jsx';
import Image from 'Components/frameworks/image/Image.jsx';
import { SliderHandler } from 'Components/frameworks/slider-handler/SliderHandler.jsx';

export type ImageSlideShowProps = {
  className?: string,
  closeLabel?: string,
  galleryLabel?: string,
  groupKey?: string,
  hasControlsOnHover?: boolean,
  hasFullScreenMode?: boolean,
  hasInfiniteScroll?: boolean,
  hasInsetBorder?: boolean,
  hasLazyLoading?: boolean,
  media: Array<{
    alt?: string,
    src: string,
  }>,
  nextLabel?: string,
  previousLabel?: string,
  ratio?: string,
};

export function ImageSlideShow(props: ImageSlideShowProps) {
  const {
    className,
    closeLabel,
    galleryLabel,
    groupKey = 'thumbnails',
    hasControlsOnHover,
    hasFullScreenMode,
    hasInfiniteScroll,
    hasInsetBorder,
    hasLazyLoading,
    media = [],
    nextLabel,
    previousLabel,
    ratio = ratioUtils.XL,
  } = props;

  const [isFullScreen, setIsFullScreen] = useState(false);

  if (!media.length) return null;

  const gk = hasFullScreenMode ? null : groupKey;

  const ItemTagName = hasFullScreenMode ? 'button' : 'div';
  const itemProps = hasFullScreenMode
    ? {
        className: 'reset-button block w-full',
        onClick: () => setIsFullScreen(true),
        type: 'button',
      }
    : null;

  const items = media
    .map(({ alt = '', src }) => {
      return src ? (
        <ItemTagName key={src} {...itemProps}>
          <Image
            className={classnames({
              'inset-border-after': hasInsetBorder,
            })}
            altMsg={alt}
            groupKey={gk}
            hasLazyLoading={hasLazyLoading}
            imageUrl={src}
            key={src}
            ratioKey={ratio}
          />
        </ItemTagName>
      ) : null;
    })
    .filter(Boolean);

  return (
    <SliderHandler
      hasInfiniteScroll={hasInfiniteScroll}
      hasKeyboardSupport
      hasLazyload
      hasSwipeSupport
      items={items}
    >
      {({
        hasControls,
        isNextControlDisabled,
        isPreviousControlDisabled,
        itemsNode,
        onNext,
        onPrevious,
      }) => (
        <div className={classnames(className, 'group relative')}>
          {itemsNode}
          {hasControls && (
            <div
              className={classnames(
                'transition-opacity will-change-transform absolute flex items-center justify-between max-w-site m-auto inset-0 pointer-events-none px-3 sm:px-5 w-full z-2',
                { 'group-hover:opacity-100 opacity-0': hasControlsOnHover },
              )}
              data-testid="ImageSlideShow-controls-parent"
            >
              {!isPreviousControlDisabled && (
                <ArrowButton
                  className="rtl:mr-auto pointer-events-auto shadow-sm z-2"
                  label={previousLabel}
                  onClick={onPrevious}
                />
              )}
              {!isNextControlDisabled && (
                <ArrowButton
                  className="rtl:ml-auto pointer-events-auto shadow-sm z-2"
                  label={nextLabel}
                  onClick={onNext}
                  rotation={180}
                />
              )}
            </div>
          )}
          {hasFullScreenMode &&
            isFullScreen && (
              <Backdrop
                ariaLabel={galleryLabel}
                closeLabel={closeLabel}
                hasCloseButton
                onClose={() => setIsFullScreen(false)}
              >
                <div className="relative w-full">
                  {itemsNode}
                  {hasControls && (
                    <div className="absolute flex items-center justify-between m-auto inset-0 pointer-events-none px-3 sm:px-5 w-full">
                      {!isPreviousControlDisabled && (
                        <ArrowButton
                          className="rtl:mr-auto pointer-events-auto shadow-sm z-2"
                          label={previousLabel}
                          onClick={onPrevious}
                        />
                      )}
                      {!isNextControlDisabled && (
                        <ArrowButton
                          className="rtl:ml-auto pointer-events-auto shadow-sm z-2"
                          label={nextLabel}
                          onClick={onNext}
                          rotation={180}
                        />
                      )}
                    </div>
                  )}
                </div>
              </Backdrop>
            )}
        </div>
      )}
    </SliderHandler>
  );
}
